import { useRef } from 'react'
import './Tabs.css'

/**
 * Vertical navigation rail. `tone="dark"` is the brand surface used by the install
 * wizard; `tone="light"` is the same rail on a white panel, which is what the user
 * modals need.
 *
 * @param {{id: string, label: string, icon: import('react').ElementType}[]} items
 * @param {'dark'|'light'} [tone]
 */
export default function Tabs({ items, value, onChange, ariaLabel, tone = 'dark' }) {
  const listRef = useRef(null)

  function handleKeyDown(e) {
    let next = null
    const current = items.findIndex((item) => item.id === value)

    if (e.key === 'ArrowDown') next = (current + 1) % items.length
    else if (e.key === 'ArrowUp') next = (current - 1 + items.length) % items.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = items.length - 1
    else return

    e.preventDefault()
    onChange(items[next].id)
    listRef.current?.querySelectorAll('.tabs__item')[next]?.focus()
  }

  return (
    <div
      ref={listRef}
      className={`tabs tabs--${tone}`}
      role="tablist"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
    >
      {items.map(({ id, label, icon: Icon }) => {
        const selected = id === value
        return (
          <button
            key={id}
            type="button"
            role="tab"
            id={`tab-${id}`}
            aria-selected={selected}
            aria-controls={`panel-${id}`}
            tabIndex={selected ? 0 : -1}
            className={`tabs__item${selected ? ' tabs__item--active' : ''}`}
            onClick={() => onChange(id)}
            data-testid="tab"
            data-tab-id={id}
          >
            <span className="tabs__icon" aria-hidden="true">
              <Icon size={18} strokeWidth={2} />
            </span>
            <bdi className="tabs__label">{label}</bdi>
          </button>
        )
      })}
    </div>
  )
}
